import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useForm } from 'react-final-form'
import { FormGroup } from './renderers'

// Address autocomplete styles are in:
// app/assets/stylesheets/ratesmgr/components/_address_autocomplete.scss
//
// If you modify autocomplete styles, also update the ERB component:
// app/views/ratesmgr/billing/_address_form.html.erb

// Custom Google Places Autocomplete using the new AutocompleteSuggestion API
// This gives us full control over styling while using the latest API

const MAX_ADDRESS_LENGTH = 200 // Prevent excessive API requests
const MIN_ADDRESS_LENGTH = 3

export const fieldAddressAutocomplete = (options) => {
  const {
    error,
    title,
    input,
    hint,
    disabled,
    compact,
    fullWidth,
    className,
    oneLine,
    readOnly,
    required,
    placeholder,
    optionSources,
  } = options

  const form = useForm()
  const inputRef = useRef(null)
  const dropdownRef = useRef(null)
  const sessionTokenRef = useRef(null)
  const sessionTokenPromiseRef = useRef(null) // Prevent race condition
  const debounceTimerRef = useRef(null)

  const [localValue, setLocalValue] = useState(input.value || '')
  const [suggestions, setSuggestions] = useState([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)

  // Sync local value with React Final Form when it changes externally
  useEffect(() => {
    setLocalValue(input.value || '')
  }, [input.value])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Get or create session token for billing optimization
  // Uses promise caching to prevent race conditions when multiple calls happen simultaneously
  const getSessionToken = async () => {
    if (sessionTokenRef.current) {
      return sessionTokenRef.current
    }

    if (!sessionTokenPromiseRef.current && window.google?.maps?.places) {
      sessionTokenPromiseRef.current = window.google.maps.importLibrary('places')
        .then(({ AutocompleteSessionToken }) => {
          sessionTokenRef.current = new AutocompleteSessionToken()
          sessionTokenPromiseRef.current = null
          return sessionTokenRef.current
        })
        .catch((err) => {
          sessionTokenPromiseRef.current = null
          throw err
        })
    }

    return sessionTokenPromiseRef.current || sessionTokenRef.current
  }

  // Reset session token after place selection
  const resetSessionToken = () => {
    sessionTokenRef.current = null
  }

  // Get country restriction based on selected country
  const getCountryRestriction = () => {
    const countryId = form.getState().values.country_id
    if (!countryId || !optionSources?.countries) return null

    const country = optionSources.countries.find(c => c.value === countryId)
    return country?.iso2 || null
  }

  // Fetch suggestions from Google Places API
  const fetchSuggestions = useCallback(async (inputValue) => {
    if (!inputValue || inputValue.length < MIN_ADDRESS_LENGTH) {
      setSuggestions([])
      setShowDropdown(false)
      return
    }

    if (!window.google?.maps) {
      console.error('Google Maps not loaded')
      return
    }

    // Sanitize and truncate input
    let sanitizedInput = inputValue
      .substring(0, MAX_ADDRESS_LENGTH)
      .replace(/[\x00-\x1F\x7F]/g, '') // Remove control characters
      .trim()

    if (sanitizedInput.length < MIN_ADDRESS_LENGTH) {
      setSuggestions([])
      setShowDropdown(false)
      return
    }

    setIsLoading(true)

    try {
      const { AutocompleteSuggestion } = await window.google.maps.importLibrary('places')
      const sessionToken = await getSessionToken()

      const request = {
        input: sanitizedInput,
        sessionToken,
        language: 'en',
      }

      // Add country restriction if a country is selected
      const countryCode = getCountryRestriction()
      if (countryCode) {
        request.includedRegionCodes = [countryCode]
      }

      const { suggestions: results } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request)

      setSuggestions(results || [])
      setShowDropdown((results || []).length > 0)
      setHighlightedIndex(-1)
    } catch (err) {
      console.error('Failed to fetch autocomplete suggestions:', err)
      setSuggestions([])
    } finally {
      setIsLoading(false)
    }
  }, [optionSources])

  // Extract address data from Google Maps place object
  const extractAddressData = (place) => {
    const components = place.addressComponents || []

    const streetNumber = components.find(c => c.types.includes('street_number'))?.longText || ''
    const route = components.find(c => c.types.includes('route'))?.longText || ''
    const streetAddress = `${streetNumber} ${route}`.trim() || place.displayName || ''

    const city = components.find(c => c.types.includes('locality'))?.longText ||
      components.find(c => c.types.includes('administrative_area_level_2'))?.longText || ''

    const zipcode = components.find(c => c.types.includes('postal_code'))?.longText || ''
    const country = components.find(c => c.types.includes('country'))
    const state = components.find(c => c.types.includes('administrative_area_level_1'))

    return { streetAddress, city, zipcode, country, state }
  }

  // Handle state mapping
  const updateStateField = (state, countryId) => {
    const stateLongName = state.longText
    const stateShortName = state.shortText
    const statesKey = `states_${countryId}`
    const stateOptions = optionSources[statesKey]
    if (!stateOptions) {
      form.change('state_text', stateLongName)
      return
    }

    // Try to find state by both long name and short name (case-sensitive and case-insensitive)
    const stateOption = stateOptions.find(s =>
      s.title === stateLongName ||
      s.title === stateShortName ||
      s.title.toLowerCase() === stateLongName.toLowerCase() ||
      s.title.toLowerCase() === stateShortName.toLowerCase()
    )
    if (stateOption) {
      form.change('state_id', stateOption.value)
      form.change('state_text', '')
    } else {
      form.change('state_text', stateLongName)
      form.change('state_id', null)
    }
  }

  // Handle country mapping and form updates
  const updateCountryAndState = (country, state) => {
    if (!country || !optionSources?.countries) return

    const countryCode = country.shortText
    const countryOption = optionSources.countries.find(c =>
      c.iso2 === countryCode || c.iso3 === countryCode
    )

    if (!countryOption) return

    const currentCountryId = form.getState().values.country_id
    const countryChanged = currentCountryId !== countryOption.value

    if (countryChanged) {
      form.change('country_id', countryOption.value)
    }

    if (state) {
      if (countryChanged) {
        // Country change triggers async loadStateIfNeeded which resets state to first option.
        // Delay our state update to run after the async reset completes.
        setTimeout(() => {
          updateStateField(state, countryOption.value)
        }, 150)
      } else {
        // Country already correct, set state immediately
        updateStateField(state, countryOption.value)
      }
    }
  }

  // Handle suggestion selection
  const handleSuggestionClick = async (suggestion) => {
    const prediction = suggestion.placePrediction
    if (!prediction) return

    setShowDropdown(false)
    setSuggestions([])
    setIsLoading(true)

    try {
      const place = prediction.toPlace()

      // Fetch place details (session token is automatically included)
      await place.fetchFields({
        fields: ['addressComponents', 'displayName', 'formattedAddress', 'location']
      })

      // Reset session token after successful fetch
      resetSessionToken()

      if (!place.location) return

      const { streetAddress, city, zipcode, country, state } = extractAddressData(place)

      setLocalValue(streetAddress)

      form.batch(() => {
        input.onChange(streetAddress)
        form.change('city', city)
        form.change('zipcode', zipcode)
        updateCountryAndState(country, state)
      })
    } catch (err) {
      console.error('Failed to fetch place details:', err)
    } finally {
      setIsLoading(false)
    }
  }

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const value = e.target.value
    setLocalValue(value)
    input.onChange(value)

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Debounce the API call
    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(value)
    }, 300)
  }

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) {
      if (e.key === 'Enter') {
        e.preventDefault()
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0)
        break
      case 'Enter':
        e.preventDefault()
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[highlightedIndex])
        }
        break
      case 'Escape':
        setShowDropdown(false)
        setHighlightedIndex(-1)
        break
    }
  }

  // Handle input focus
  const handleFocus = (e) => {
    input.onFocus?.(e)
    if (suggestions.length > 0) {
      setShowDropdown(true)
    }
  }

  // Handle input blur
  const handleBlur = (e) => {
    // Delay blur to allow click on dropdown
    setTimeout(() => {
      input.onBlur?.(e)
    }, 200)
  }

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  return (
    <FormGroup
      error={error}
      title={title}
      hint={hint}
      compact={compact}
      fullWidth={fullWidth}
      className={className}
      oneLine={oneLine}
      required={required}
    >
      <div className="address-autocomplete-wrapper">
        <input
          ref={inputRef}
          name={input.name}
          type="text"
          disabled={disabled}
          readOnly={readOnly}
          placeholder={placeholder || "Start typing your address..."}
          className="form-control"
          autoComplete="off"
          value={localValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          role="combobox"
          aria-expanded={showDropdown && suggestions.length > 0}
          aria-autocomplete="list"
          aria-controls="address-suggestions-list"
          aria-activedescendant={highlightedIndex >= 0 ? `address-suggestion-${highlightedIndex}` : undefined}
        />

        {showDropdown && suggestions.length > 0 && (
          <ul
            ref={dropdownRef}
            id="address-suggestions-list"
            role="listbox"
            className="address-suggestions-dropdown"
            aria-label="Address suggestions"
          >
            {suggestions.map((suggestion, index) => {
              const prediction = suggestion.placePrediction
              if (!prediction) return null

              return (
                <li
                  id={`address-suggestion-${index}`}
                  key={prediction.placeId}
                  role="option"
                  aria-selected={index === highlightedIndex}
                  className={`address-suggestion-item ${index === highlightedIndex ? 'highlighted' : ''}`}
                  onClick={() => handleSuggestionClick(suggestion)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <span className="address-suggestion-main">
                    {prediction.mainText?.text || prediction.text?.text}
                  </span>
                  {prediction.secondaryText?.text && (
                    <span className="address-suggestion-secondary">
                      {prediction.secondaryText.text}
                    </span>
                  )}
                </li>
              )
            })}
          </ul>
        )}

        {isLoading && (
          <div className="address-autocomplete-loading">
            <span className="address-autocomplete-spinner" />
          </div>
        )}
      </div>
    </FormGroup>
  )
}
